import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { examsService } from '@/services/exams.service';
import { coursesService } from '@/services/courses.service';
import { Plus, Search, Edit, Trash2, GraduationCap, Clock, FileText, Award } from 'lucide-react';
import { ViewToggle } from '@/components/ViewToggle';
import { PaginationControls } from '@/components/PaginationControls';
import { PageHeader } from '@/components/PageHeader';
import { Tooltip } from '@/components/Tooltip';
import { EmptyState } from '@/components/EmptyState';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';

export function CoursesList() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [examFilter, setExamFilter] = useState('all');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [paginationMode, setPaginationMode] = useState<'pagination' | 'infinite'>('pagination');
  const [currentPage, setCurrentPage] = useState(1);
  const [displayCount, setDisplayCount] = useState(15);
  const itemsPerPage = 15;

  const { data: coursesData, isLoading } = useQuery({
    queryKey: ['courses', { search, exam: examFilter }],
    queryFn: async () => {
      try {
        return await coursesService.getCourses({
          search,
          exam_id: examFilter === 'all' ? undefined : examFilter
        });
      } catch (error) {
        console.error('Failed to fetch courses:', error);
        return [];
      }
    },
  });

  const allCourses = coursesData || [];
  const totalItems = allCourses.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Display courses based on mode
  const courses = useMemo(() => {
    if (paginationMode === 'infinite') {
      return allCourses.slice(0, displayCount);
    } else {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return allCourses.slice(startIndex, endIndex);
    }
  }, [allCourses, paginationMode, displayCount, currentPage, itemsPerPage]);

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + itemsPerPage);
  };

  const handlePaginationModeChange = (mode: 'pagination' | 'infinite') => {
    setPaginationMode(mode);
    if (mode === 'pagination') {
      setCurrentPage(1);
    } else {
      setDisplayCount(itemsPerPage);
    }
  };

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
    setDisplayCount(itemsPerPage);
  };

  const handleExamFilterChange = (value: string) => {
    setExamFilter(value);
    setCurrentPage(1);
    setDisplayCount(itemsPerPage);
  };

  const deleteMutation = useMutation({
    mutationFn: async (courseId: string) => {
      return await coursesService.deleteCourse(courseId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      showDeleteSuccess('Course has been deleted successfully.');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to delete course';
      showDeleteError(message);
    },
  });

  const handleDeleteCourse = async (courseId: string, courseName: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete Course?',
      itemName: courseName,
    });

    if (confirmed) {
      deleteMutation.mutate(courseId);
    }
  };

  const { data: exams } = useQuery({
    queryKey: ['exams'],
    queryFn: async () => {
      try {
        return await examsService.getExams();
      } catch (error) {
        console.error('Failed to fetch exams:', error);
        return [];
      }
    },
  });

  // Calculate unique exams count
  const uniqueExams = new Set(allCourses.flatMap(c => c.linked_exams)).size;
  const totalMaterials = allCourses.reduce((sum, c) => sum + (c.materials_count || 0), 0);
  const totalHours = allCourses.reduce((sum, c) => sum + (c.total_hours || 0), 0);

  return (
    <div className="space-y-6">
      {/* Page Header with Breadcrumbs */}
      <div className="flex items-start justify-between">
        <PageHeader
          title="Courses"
          description="Manage courses and their content structure"
          breadcrumbs={[{ label: 'Content Management', path: '#' }, { label: 'Courses' }]}
        />
        <Tooltip content="Create a new course">
          <Link to="/courses/create" className="btn-primary flex items-center mt-10">
            <Plus className="w-4 h-4 mr-2" />
            Create Course
          </Link>
        </Tooltip>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Tooltip content="Total number of courses">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Courses</p>
                <p className="text-3xl font-bold text-gray-900">{allCourses.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <GraduationCap className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </div>
        </Tooltip>
        <Tooltip content="Total learning materials across all courses">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Learning Materials</p>
                <p className="text-3xl font-bold text-primary-600">{totalMaterials.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <FileText className="w-8 h-8 text-primary-600" />
              </div>
            </div>
          </div>
        </Tooltip>
        <Tooltip content="Total course hours">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Hours</p>
                <p className="text-3xl font-bold text-green-600">{totalHours.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Clock className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>
        </Tooltip>
        <Tooltip content="Unique exams covered">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Unique Exams</p>
                <p className="text-3xl font-bold text-orange-600">{uniqueExams}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Award className="w-8 h-8 text-orange-600" />
              </div>
            </div>
          </div>
        </Tooltip>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex items-center space-x-4 flex-1">
            <Tooltip content="Search courses by name, ID, or description">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute w-5 h-5 text-gray-400 left-3 top-2.5" />
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-10 input-field"
                  />
                </div>
              </div>
            </Tooltip>
            <Tooltip content="Filter courses by linked exam">
              <select
                value={examFilter}
                onChange={(e) => handleExamFilterChange(e.target.value)}
                className="input-field w-64"
              >
                <option value="all">All Exams</option>
                {(Array.isArray(exams) ? exams : (exams as any)?.exams || []).map((exam: any) => (
                  <option key={exam.exam_id} value={exam.exam_id}>
                    {exam.exam_id} - {exam.name}
                  </option>
                ))}
              </select>
            </Tooltip>
            {(search || examFilter !== 'all') && (
              <Tooltip content="Clear all filters">
                <button
                  onClick={() => {
                    setSearch('');
                    setExamFilter('all');
                    setCurrentPage(1);
                  }}
                  className="btn-secondary whitespace-nowrap"
                >
                  Reset Filters
                </button>
              </Tooltip>
            )}
          </div>
          <ViewToggle view={view} onViewChange={setView} />
        </div>
      </div>

      {/* Courses Content */}
      <div className="card">
        {isLoading ? (
          <div className="py-12 text-center text-gray-500">Loading...</div>
        ) : !courses || courses.length === 0 ? (
          allCourses.length === 0 ? (
            <EmptyState
              icon={GraduationCap}
              title="No courses yet"
              description="Get started by creating your first course. Define course details, link exams, and manage learning materials."
              action={{
                label: 'Create Your First Course',
                to: '/courses/create'
              }}
            />
          ) : (
            <EmptyState
              icon={Search}
              title="No courses match your filters"
              description={`Try adjusting your search or filters. ${allCourses.length} total courses available.`}
              action={{
                label: 'Reset Filters',
                onClick: () => {
                  setSearch('');
                  setExamFilter('all');
                }
              }}
              secondaryAction={{
                label: 'View All Courses',
                onClick: () => {
                  setSearch('');
                  setExamFilter('all');
                }
              }}
            />
          )
        ) : view === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <div key={course.course_id} className="group card hover:shadow-xl transition-all duration-300 border-l-4 border-primary-500 relative overflow-hidden">
                {/* Gradient Overlay */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-50 to-transparent rounded-bl-full opacity-50" />

                {/* Course Header */}
                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg">
                        <GraduationCap className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-lg truncate group-hover:text-primary-600 transition-colors">
                          {course.name}
                        </h3>
                        <p className="text-sm text-gray-500 font-mono">{course.course_id}</p>
                      </div>
                    </div>
                  </div>

                  {/* Linked Exams Badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {course.linked_exams.slice(0, 3).map((exam) => (
                      <span key={exam} className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full border border-primary-200">
                        {exam}
                      </span>
                    ))}
                    {course.linked_exams.length > 3 && (
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                        +{course.linked_exams.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* Course Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <Tooltip content="Course provider">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Provider</p>
                        <p className="text-sm font-semibold text-gray-900 truncate">{course.provider}</p>
                      </div>
                    </Tooltip>
                    <Tooltip content="Learning modality">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Modality</p>
                        <p className="text-sm font-semibold text-gray-900">{course.modality}</p>
                      </div>
                    </Tooltip>
                    <Tooltip content="Total course hours">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <Clock className="w-4 h-4 mx-auto text-gray-400 mb-1" />
                        <p className="text-sm font-semibold text-gray-900">{course.total_hours || 0}h</p>
                      </div>
                    </Tooltip>
                  </div>

                  {/* Description */}
                  {course.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                      {course.description}
                    </p>
                  )}

                  {/* Materials Count Badge */}
                  {course.materials_count !== undefined && course.materials_count > 0 && (
                    <div className="flex items-center justify-center space-x-2 mb-4 p-3 bg-gradient-to-r from-blue-50 to-primary-50 rounded-lg border border-primary-100">
                      <FileText className="w-5 h-5 text-primary-600" />
                      <span className="text-sm font-semibold text-primary-700">
                        {course.materials_count.toLocaleString()} learning materials
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center space-x-2 pt-4 border-t border-gray-200">
                    <Tooltip content="Edit course details">
                      <Link
                        to={`/courses/${course.course_id}/edit`}
                        className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center group-hover:bg-primary-50 group-hover:text-primary-700 group-hover:border-primary-300 transition-colors"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Link>
                    </Tooltip>
                    <Tooltip content="Delete this course">
                      <button
                        onClick={() => handleDeleteCourse(course.course_id, course.name)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-gray-200">
                  <th className="pb-3 text-sm font-semibold text-gray-600">Course ID</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600">Name</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600">Exams</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600">Provider</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600">Modality</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600">Duration</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600">Materials</th>
                  <th className="pb-3 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course) => (
                  <tr key={course.course_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4">
                      <span className="font-mono text-sm font-medium text-gray-900">{course.course_id}</span>
                    </td>
                    <td className="py-4">
                      <div>
                        <p className="font-medium text-gray-900">{course.name}</p>
                        {course.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{course.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-4">
                      <span className="text-sm text-gray-600">{course.linked_exams.join(', ')}</span>
                    </td>
                    <td className="py-4">
                      <span className="text-sm text-gray-600">{course.provider}</span>
                    </td>
                    <td className="py-4">
                      <span className="text-sm text-gray-600">{course.modality}</span>
                    </td>
                    <td className="py-4">
                      <span className="text-sm text-gray-600">{course.total_hours || 0}h</span>
                    </td>
                    <td className="py-4">
                      <span className="text-sm font-medium text-primary-600">
                        {course.materials_count || 0}
                      </span>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/courses/${course.course_id}/edit`}
                          className="p-2 text-gray-600 transition-colors hover:text-primary-600 hover:bg-gray-100 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDeleteCourse(course.course_id, course.name)}
                          className="p-2 text-gray-600 transition-colors hover:text-red-600 hover:bg-gray-100 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalItems > 0 && (
        <PaginationControls
          mode={paginationMode}
          onModeChange={handlePaginationModeChange}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onLoadMore={handleLoadMore}
          hasMore={displayCount < totalItems}
          isLoading={isLoading}
        />
      )}

    </div>
  );
}
